/** Shapes returned by the API. Mirrors /api/src/constant/enums.js. */

export type UserStatus = 'pending_review' | 'active' | 'banned' | 'rejected';
export type AdminRole = 'support' | 'admin' | 'superadmin';
export type DrawStatus = 'open' | 'locked' | 'drawn' | 'settled' | 'void';
/** `number` is a straight pick and carries `pickedNumber`; the rest are even money. */
export type BetType = 'number' | 'big' | 'small' | 'odd' | 'even';
export type BetStatus = 'placed' | 'won' | 'lost' | 'refunded' | 'voided';
/**
 * What a ledger row says happened to the money.
 *
 * `credit_adjustment` is an operator setting a balance to a figure — a
 * correction, which is neither money arriving nor money leaving, and is kept
 * apart from `top_up` and `withdrawal` so it is not counted as a deposit.
 *
 * `admin_adjustment` is retired and never written; it stays in the union so
 * rows from an earlier version still type-check on a database that has not been
 * through the boot-time migration yet.
 */
export type TransactionType =
  | 'top_up'
  | 'withdrawal'
  | 'credit_adjustment'
  | 'bet_stake'
  | 'bet_payout'
  | 'bet_refund'
  /** @deprecated Retired. Reclassified at boot; never written. */
  | 'admin_adjustment';
export type TransactionStatus = 'pending' | 'processing' | 'success' | 'failed' | 'rejected';

export type PermissionAction = 'view' | 'create' | 'update' | 'delete';

/** module → action → allowed. The server sends the module list with it. */
export type PermissionGrid = Record<string, Partial<Record<PermissionAction, boolean>>>;

export interface PermissionModule {
  label: string;
  /** action → what it actually does, for the switch's description. */
  actions: Partial<Record<PermissionAction, string>>;
}

export interface PermissionCatalogue {
  roles: AdminRole[];
  modules: Record<string, PermissionModule>;
  grids: Record<string, PermissionGrid>;
  defaults: Record<string, PermissionGrid>;
}

export interface AdminProfile {
  id: string;
  username: string;
  displayName: string;
  role: AdminRole;
  isActive: boolean;
  lastLoginAt: string | null;
  /**
   * What this operator may do, as the API sees it.
   *
   * Used to decide what the console offers. It is never the control — a screen
   * hidden here is still a route refused there, and every permission is checked
   * again on the request.
   */
  permissions?: PermissionGrid;
}

export interface AdminUser {
  id: string;
  referralCode?: { id: string; code: string } | null;
  username: string;
  fullName: string;
  phone?: string | null;
  address?: string | null;
  /** Bank details, as typed on the signup form. */
  bankName?: string | null;
  bankAccountNo?: string | null;
  /** The recipe for this account's generated history, if it has one. */
  historyProfile?: HistoryProfile | null;
  status: UserStatus;
  balanceCents: number;
  isTest: boolean;
  locale: string;
  approvedAt: string | null;
  lastLoginAt: string | null;
  createdAt: string;
}

export interface Draw {
  drawNo: number;
  periodCode: string;
  positionCount: number;
  numberRange: { min: number; max: number };
  oddsBasisPoints: number;
  /** How a picked number was judged on this round. Snapshotted at creation. */
  numberBetRule: 'exact' | 'big_small';
  opensAt: string;
  closesAt: string;
  /** How long before the close this round stopped taking bets. 0 = no lockdown. */
  lockdownSeconds: number;
  /** When the book shut — `closesAt` minus `lockdownSeconds`. */
  betsCloseAt: string;
  /** Whether a slip would still be accepted. The server rechecks on placement. */
  isBettingOpen: boolean;
  status: DrawStatus;
  results: number[];
  drawnAt: string | null;
  settledAt: string | null;
  isManualResult: boolean;
}

/** The admin draw list returns raw documents, which carry `_id` and totals. */
export interface AdminDraw extends Omit<Draw, 'numberRange' | 'isManualResult'> {
  _id: string;
  numberMin: number;
  numberMax: number;
  resultSource?: 'auto' | 'manual';
  /** Written to carry generated history — never the current round. */
  isBackfilled?: boolean;
  totals?: { betCount: number; stakeCents: number; payoutCents: number; netCents: number };
  voidReason?: string;
}

export interface Bet {
  id: string;
  drawNo: number;
  position: number;
  betType: BetType;
  /** The number chosen, for a straight `number` bet. Null for the others. */
  pickedNumber: number | null;
  stakeCents: number;
  oddsBasisPoints: number;
  status: BetStatus;
  payoutCents: number;
  resultNumber: number | null;
  settledAt: string | null;
  createdAt: string;
  user?: { id: string; username: string; isTest: boolean } | null;
}

export interface WalletTransaction {
  id: string;
  reference: string;
  type: TransactionType;
  amountCents: number;
  balanceAfterCents: number;
  /** Behind a deleted bet or a voided round — not shown in the player app. */
  hiddenFromPlayer?: boolean;
  status: TransactionStatus;
  reason: string | null;
  createdAt: string;
}

export interface AuditEntry {
  id: string;
  admin: { id: string; username: string };
  action: string;
  target: {
    type: string;
    id: string;
    /** Resolved by the API: the player, round, ledger reference behind the id. */
    label?: { title: string | null; detail?: string | null; amountCents?: number } | null;
  } | null;
  before: Record<string, unknown> | null;
  after: Record<string, unknown> | null;
  reason: string | null;
  ip: string | null;
  createdAt: string;
}

export interface ReferralCode {
  id: string;
  signupCount?: number;
  remainingUses?: number;
  code: string;
  note: string | null;
  maxUses: number;
  useCount: number;
  expiresAt: string | null;
  isActive: boolean;
  usable: boolean;
  createdAt: string;
}

/** A bank a player may hold an account with. `name` is what is stored. */
export interface Bank {
  code: string;
  name: string;
}

export interface Game {
  id: string;
  slug: string;
  name: string;
  subtitle: string;
  badge: string | null;
  isPlayable: boolean;
  sortOrder: number;
}

export interface ReportSummary {
  betCount: number;
  turnoverCents: number;
  payoutCents: number;
  grossProfitCents: number;
  marginPercent: number;
  drawCount: number;
  excludeTest: boolean;
}

export interface OnlineAccount {
  id: string;
  username: string;
  connections: number;
}

export interface SystemStats {
  realtime: {
    connections: number;
    players: number;
    admins: number;
    online: { players: OnlineAccount[]; admins: OnlineAccount[] };
  };
  cache: { enabled: boolean; entries: number; maxEntries: number };
  uptimeSeconds: number;
}

export interface GameRules {
  intervalSeconds: number;
  /** Configured lockdown window. A round in flight keeps the one it opened under. */
  lockdownSeconds: number;
  positionCount: number;
  numberRange: { min: number; max: number };
  oddsBasisPoints: number;
  oddsMultiplier: number;
  /**
   * What a picked number pays.
   *
   * A pick stands for the half it falls in — back 6 on a 1–10 round and any of
   * 6–10 wins it — so it is an even-money shot at the even-money price. Read it
   * from here rather than assuming either, so a rule change reaches the console.
   */
  numberOddsBasisPoints: number;
  numberOddsMultiplier: number;
  /** How a picked number is judged on rounds opened from now on. */
  numberBetRule?: 'exact' | 'big_small';
  betTypes: Record<
    string,
    { label: string; wins: string; oddsBasisPoints?: number; takesNumber?: boolean }
  >;
  stakeLimits: { minCents: number; maxCents: number };
  /** False when the game's rounds are switched off in the console. */
  isRunning?: boolean;
}

/**
 * Whose actions a trail contains.
 *
 * `own` for anyone below superadmin — the API scopes it, so this is the console
 * reporting what it was given rather than deciding anything.
 */
export type AuditScope = 'own' | 'all';

export interface Paged<T> {
  items: T[];
  total: number;
  page: number;
  perPage: number;
  pages: number;
}

export type Settings = Record<string, unknown>;

/** One locale of the rules-and-tutorial booklet players read in the popup. */
export interface RulesGuideDocument {
  title: string;
  /** The lead paragraph, shown above the sections. */
  intro: string;
  /** Rendered in order — the operator controls the sequence. */
  sections: { heading: string; body: string }[];
}

/**
 * The booklet as the console reads it: both languages, and the version.
 *
 * `version` is the field the player app's popup keys off — it moves once per
 * publish, so a client that remembers the last version it showed raises the
 * booklet exactly once for a change. Worth saying out loud in the console,
 * because it means a one-character correction re-interrupts every player.
 */
export interface RulesGuide extends RulesGuideDocument {
  locale: "en" | "zh";
  version: number;
  updatedAt: string | null;
  documents: { en: RulesGuideDocument; zh: RulesGuideDocument };
}

/** One entry in a game's queue of pre-drawn results. */
export interface UpcomingRound {
  id: string;
  /** Position in the queue. Consumed lowest first. */
  sequence: number;
  numbers: number[];
  source: "auto" | "manual";
  isManual: boolean;
  /**
   * False for hand-set numbers the game has since outgrown — a changed range
   * or position count. They are kept rather than overwritten, but a round that
   * reached them would draw fresh numbers instead.
   */
  fitsCurrentSettings: boolean;
  drawNo: number;
  /** Set only for the first entry, when a round is already open for it. */
  periodCode: string | null;
  /** True for every entry but the one belonging to the round counting down. */
  isProjected: boolean;
  drawsAt: string;
  updatedAt: string;
  updatedBy: string | null;
}

export interface UpcomingRounds {
  positionCount: number;
  numberRange: { min: number; max: number };
  intervalSeconds: number;
  /** False when the game is paused — the times are then guesses. */
  isRunning: boolean;
  items: UpcomingRound[];
}

/** Sunday is 0, matching the API and `Date.prototype.getDay`. */
export type DayOfWeek = 0 | 1 | 2 | 3 | 4 | 5 | 6;

/** An inclusive pair. Equal ends mean "exactly this". */
export interface Range {
  min: number;
  max: number;
}

/**
 * How a playing day's bets and wins interleave.
 *
 * Every playing day is Deposit → bets → Withdraw. `each` puts each win right
 * after its bet (Deposit → Bet 1 → Win 1 → Bet 2 → Win 2 → Withdraw);
 * `together` places the bets back to back and lets the wins follow together.
 */
export interface HistorySequence {
  settle: "each" | "together";
  /** The pause between one record and the next, in minutes. */
  gapMinutes: Range;
}

/** One record of a previewed day, with its time in the profile's zone. */
export interface HistorySampleEvent {
  kind: "deposit" | "bet" | "payout" | "withdrawal";
  time: string;
  amountCents: number;
  won?: boolean;
}

/** The recipe for a player's generated history. Stored on the account. */
export interface HistoryProfile {
  from: string;
  to: string;
  pattern: "weekdays" | "interval";
  daysOfWeek: number[];
  /** `interval`: the gap between playing days. Equal ends mean exactly that many. */
  intervalDays: Range;
  /** @deprecated What profiles saved before `intervalDays` hold. */
  everyNDays?: number;
  betsPerDay: Range;
  /** IANA zone every clock time here is read in — the operator's. */
  timeZone: string;
  /** When in the day every record falls, `HH:mm`. An end before the start runs over midnight. */
  window: { start: string; end: string };
  /** @deprecated Whole hours, what profiles saved before `window` hold. */
  hours?: Range;
  sequence: HistorySequence;
  stakeCents: Range;
  /** Allocated as an exact quota over the run, not rolled per bet. */
  winRatePercent: number;
  /**
   * Share of playing days that open with a deposit and close with a withdrawal;
   * the rest are bets and wins only. An exact quota. 100 when absent.
   */
  cashFlowPercent: number;
  /** One deposit opens every cash-flow day; only the amount is set. */
  deposits: {
    amountCents: Range;
    /** Raise a day's deposit when its play or its closing withdrawal would not clear. */
    autoTopUp: boolean;
  };
  /** One withdrawal closes every cash-flow day; only the amount is set. */
  withdrawals: {
    percentOfBalance: Range;
    minCents: number;
  };
  /** Null until the profile has been run. */
  lastGeneratedAt?: string | null;
}

/**
 * What the plan called for, and what was written.
 *
 * On a clean run the `...Planned` figures and the `...Created` ones agree. A
 * preview reports the planned side with the created side at zero.
 */
export interface HistoryResult {
  betsPlanned: number;
  winsPlanned: number;
  playingDays: number;
  betsCreated: number;
  betsWon: number;
  stakedCents: number;
  paidCents: number;
  netCents: number;
  depositCount: number;
  depositedCents: number;
  withdrawalCount: number;
  withdrawnCents: number;
  balanceCents: number;
  firstAt: string | null;
  lastAt: string | null;
  timeZone: string;
  window: { start: string; end: string };
  /** The first few days, record by record, as the player's history will read. */
  sample: { date: string; events: HistorySampleEvent[] }[];
  /** What `replace` removed before this run, if anything. */
  cleared: { bets: number; transactions: number; rounds: number } | null;
  truncated: boolean;
  /** Anything the generator had to adjust to make the profile work. */
  warnings: string[];
}

export interface LedgerRow extends WalletTransaction {
  user?: { id: string; username: string; isTest: boolean } | null;
}

export interface ResultEditResult {
  draw: Draw;
  previousResults: number[];
  /** True when the round already had a result before this edit. */
  wasSettled: boolean;
  betsRejudged: number;
  betsChanged: number;
  totals: { betCount: number; stakeCents: number; payoutCents: number; netCents: number };
}

/** What changing a bet's selection or stake did to the player and the round. */
export interface BetEditResult {
  bet: Bet;
  /** Positive when the player paid more, negative when stake came back. */
  stakeDelta: number;
  totals: { betCount: number; stakeCents: number; payoutCents: number; netCents: number };
  periodCode: string;
}

/** Roll-up across everything a draw filter matched, not just the visible page. */
export interface DrawSummary {
  betCount: number;
  stakeCents: number;
  payoutCents: number;
  /** Stake minus payout, from the house's side. Positive = the round made money. */
  netCents: number;
}

export interface GameConfig {
  positionCount: number;
  numberMin: number;
  numberMax: number;
  oddsBasisPoints: number;
  intervalSeconds: number;
  /**
   * The lockdown window: how long before a round draws that betting stops.
   *
   * Zero switches it off. Must be shorter than `intervalSeconds`, or the round
   * would have no window to take bets in — the API rejects the pair with
   * `INVALID_LOCKDOWN_WINDOW`.
   */
  lockdownSeconds: number;
}

/**
 * What tapping a game-hall tile does.
 *
 * The single control the console offers for a tile: it decides both what the
 * player app animates and, on the API's side, whether the game is playable at
 * all. There is no separate playable switch.
 */
export const GAME_INTERACTIONS = [
  'clickable',
  'disabled',
  'fake_sign_in',
  'coming_soon',
] as const;

export type GameInteraction = (typeof GAME_INTERACTIONS)[number];

export interface AdminGame {
  id: string;
  slug: string;
  name: string;
  subtitle: string;
  badge: string | null;
  tileImage: string | null;
  /**
   * There is a game behind this tile — the scheduler can run it and bets can be
   * placed on it.
   *
   * Derived by the API from `interaction`, which is the one control the console
   * offers: `clickable` means playable, anything else does not. Read it; do not
   * send it.
   */
  isPlayable: boolean;
  /** What tapping the tile does in the player hall. */
  interaction: GameInteraction;
  /**
   * Withheld from the player hall entirely.
   *
   * Stronger than `isPlayable`: the public `/games` payload drops the row, over
   * REST and over the socket alike. The console still lists it, which is what
   * makes hiding reversible where deleting is not.
   */
  isHidden: boolean;
  /** The scheduler is opening rounds for this game. */
  isRunning: boolean;
  config: GameConfig;
  liveRound: {
    drawNo: number;
    periodCode: string;
    closesAt: string;
    /** The window this round opened under, not necessarily the game's current one. */
    lockdownSeconds: number;
    betsCloseAt: string;
    isBettingOpen: boolean;
    positionCount: number;
    /** What voiding the round would refund — shown before the operator does. */
    betCount?: number;
    stakeCents?: number;
  } | null;
  lastResult: { drawNo: number; periodCode: string; results: number[]; settledAt: string } | null;
  roundCount: number;
  sortOrder: number;
}

export type SessionStatus = 'active' | 'idle' | 'expired' | 'revoked';

export interface AdminSession {
  id: string;
  principalType: 'admin' | 'player';
  principalId: string | null;
  username: string;
  role: string | null;
  status: SessionStatus;
  createdAt: string;
  lastSeenAt: string;
  expiresAt: string;
  idleExpiresAt: string;
  revokedAt: string | null;
  revokedReason: string | null;
  ip: string | null;
  userAgent: string | null;
}
